// Admin Call Scheduling API client.
// Mirrors the project's service convention (see services/scheduledMessageService.js):
// plain axios, REACT_APP_API_URL base, Bearer token from localStorage.
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL;            // https://api.mrpatchasphalt.com/api
const API_PUBLIC = process.env.REACT_APP_API_PUBLIC;      // https://api.mrpatchasphalt.com

const getAuthToken = () => localStorage.getItem('token');

const getHeaders = () => ({
  headers: {
    Authorization: `Bearer ${getAuthToken()}`,
  },
});

const buildQuery = (params = {}) =>
  new URLSearchParams(
    Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== '')
  ).toString();

/* ----------------------------------- Bookings ----------------------------------- */

// params: { search, status, from, to, sort, order, limit, offset, lead_id }
// -> { success, total, calls: [ ScheduledCall + minimal lead ] }
export const listCalls = async (params = {}) => {
  try {
    const qs = buildQuery(params);
    const response = await axios.get(`${API_URL}/call-schedule/calls${qs ? `?${qs}` : ''}`, getHeaders());
    return response.data;
  } catch (error) {
    console.error('Error fetching calls:', error);
    throw error;
  }
};

// -> { success, call }
export const getCall = async (id) => {
  try {
    const response = await axios.get(`${API_URL}/call-schedule/calls/${id}`, getHeaders());
    return response.data;
  } catch (error) {
    console.error('Error fetching call:', error);
    throw error;
  }
};

// body: { slot_start (ISO), slot_end?, name?, email?, phone?, lead_id?, notes? }
// NOTE: admin-created bookings do NOT send a confirmation SMS/email.
export const createCall = async (body) => {
  try {
    const response = await axios.post(`${API_URL}/call-schedule/calls`, body, getHeaders());
    return response.data;
  } catch (error) {
    console.error('Error creating call:', error);
    throw error;
  }
};

// body: any of { name, email, phone, notes, slot_start, slot_end }
// Changing slot_start re-arms the reminder.
export const updateCall = async (id, body) => {
  try {
    const response = await axios.put(`${API_URL}/call-schedule/calls/${id}`, body, getHeaders());
    return response.data;
  } catch (error) {
    console.error('Error updating call:', error);
    throw error;
  }
};

// status: scheduled | completed | canceled | no_show
export const setCallStatus = async (id, status) => {
  try {
    const response = await axios.post(`${API_URL}/call-schedule/calls/${id}/status`, { status }, getHeaders());
    return response.data;
  } catch (error) {
    console.error('Error updating call status:', error);
    throw error;
  }
};

export const deleteCall = async (id) => {
  try {
    const response = await axios.delete(`${API_URL}/call-schedule/calls/${id}`, getHeaders());
    return response.data;
  } catch (error) {
    console.error('Error deleting call:', error);
    throw error;
  }
};

/* ----------------------------------- Dashboard ---------------------------------- */

// params: { upcoming?, recent? } -> { success, upcoming: [...], recent: [...] }
export const getCallDashboard = async (params = {}) => {
  try {
    const qs = buildQuery(params);
    const response = await axios.get(`${API_URL}/call-schedule/dashboard${qs ? `?${qs}` : ''}`, getHeaders());
    return response.data;
  } catch (error) {
    console.error('Error fetching call dashboard:', error);
    throw error;
  }
};

/* ------------------------------- Availability rules ----------------------------- */

export const listRules = async () => {
  try {
    const response = await axios.get(`${API_URL}/call-schedule/rules`, getHeaders());
    return response.data;
  } catch (error) {
    console.error('Error fetching rules:', error);
    throw error;
  }
};

// body: { day_of_week, start_time, end_time, slot_minutes?, is_active? }
export const createRule = async (body) => {
  try {
    const response = await axios.post(`${API_URL}/call-schedule/rules`, body, getHeaders());
    return response.data;
  } catch (error) {
    console.error('Error creating rule:', error);
    throw error;
  }
};

export const updateRule = async (id, body) => {
  try {
    const response = await axios.put(`${API_URL}/call-schedule/rules/${id}`, body, getHeaders());
    return response.data;
  } catch (error) {
    console.error('Error updating rule:', error);
    throw error;
  }
};

export const deleteRule = async (id) => {
  try {
    const response = await axios.delete(`${API_URL}/call-schedule/rules/${id}`, getHeaders());
    return response.data;
  } catch (error) {
    console.error('Error deleting rule:', error);
    throw error;
  }
};

/* ----------------------------------- Blocks ------------------------------------- */

// all=true also returns past blocks
export const listBlocks = async (all = false) => {
  try {
    const qs = all ? '?all=true' : '';
    const response = await axios.get(`${API_URL}/call-schedule/blocks${qs}`, getHeaders());
    return response.data;
  } catch (error) {
    console.error('Error fetching blocks:', error);
    throw error;
  }
};

// body: { starts_at (ISO), ends_at (ISO), reason? }
export const createBlock = async (body) => {
  try {
    const response = await axios.post(`${API_URL}/call-schedule/blocks`, body, getHeaders());
    return response.data;
  } catch (error) {
    console.error('Error creating block:', error);
    throw error;
  }
};

export const updateBlock = async (id, body) => {
  try {
    const response = await axios.put(`${API_URL}/call-schedule/blocks/${id}`, body, getHeaders());
    return response.data;
  } catch (error) {
    console.error('Error updating block:', error);
    throw error;
  }
};

export const deleteBlock = async (id) => {
  try {
    const response = await axios.delete(`${API_URL}/call-schedule/blocks/${id}`, getHeaders());
    return response.data;
  } catch (error) {
    console.error('Error deleting block:', error);
    throw error;
  }
};

/* ------------------------- Public availability (slot preview) ------------------- */

// Used by the booking modal to offer real in-hours slots. No auth.
// days clamped 1-7 by the API. -> { success, timezone, slots: [{start,end}] }
export const getAvailability = async (days = 7) => {
  try {
    const response = await axios.get(`${API_PUBLIC}/schedule/availability?days=${days}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching availability:', error);
    throw error;
  }
};
